import React from "react";
import { IonIcon } from "@ionic/react";
import {
  closeOutline,
  timeOutline,
  eyeOutline,
  checkmarkCircleOutline,
  mailOutline,
  callOutline,
  personOutline,
  removeCircleOutline
} from "ionicons/icons";
import { FollowerListItemProps, DisplayDataSharingFollower } from "./DataSharingTypes";

const FollowerListItem: React.FC<FollowerListItemProps> = ({
  follower,
  onRemove,
  showStatus = false,
  onStatusClick,
  itemType = "follower",
  showEmail = false,
  showPhone = false,
  isLoading = false,
}) => {
  const [showInitials, setShowInitials] = React.useState(false);
  // Helper functions for display
  // ⛔ Both helpers destructured `profile` off the follower and then dereferenced it. This is a
  // pure render path — App.tsx's ErrorBoundary is the app's ONLY one and renders `null`
  // (TD-165), so one profile-less row here blanks the WHOLE app, web and native. Guard the
  // OBJECT, not only its fields (D143.5).
  const profile = follower?.profile;

  const getDisplayName = (): string => {
    if (!profile) return "Unknown User";
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile.first_name || profile.last_name || profile.username || profile.email || profile.phone || "Unknown User";
  };

  const getInitials = (): string => {
    if (!profile) return "U";
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name.charAt(0)}${profile.last_name.charAt(0)}`.toUpperCase();
    }
    if (profile.first_name) {
      return profile.first_name.charAt(0).toUpperCase();
    }
    if (profile.last_name) {
      return profile.last_name.charAt(0).toUpperCase();
    }
    if (profile.username) {
      return profile.username.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const getAvatarColor = (): string => {
    switch (itemType) {
      case "follower":
        return "bg-cyan-600";
      case "following":
        return "bg-green-600";
      case "pending_incoming":
        return "bg-amber-600";
      case "pending_outgoing":
        return "bg-blue-600";
      default:
        return "bg-gray-600";
    }
  };

  const getStatusDisplay = () => {
    if (follower.status === "pending") {
      const isPendingIncoming = itemType === "pending_incoming";
      const isPendingOutgoing = itemType === "pending_outgoing";

      return (
        <div className={`flex items-center text-sm ${isPendingIncoming ? "text-amber-600" :
          isPendingOutgoing ? "text-blue-600" : "text-gray-600"
          }`}>
          <IonIcon icon={timeOutline} className="mr-1" style={{ fontSize: "14px" }} />
          <span>
            {isPendingIncoming ? "Wants to follow you" :
              isPendingOutgoing ? "Awaiting approval" : "Pending"}
          </span>
        </div>
      );
    }
    return null;
  };

  const getActionButton = () => {
    // For pending requests handled by parent components, don't show action button
    if (itemType === "pending_incoming" || itemType === "pending_outgoing") {
      return null;
    }

    // For status click interactions
    if (showStatus && follower.status === "pending" && onStatusClick) {
      return (
        <button
          className="text-blue-500 text-sm cursor-pointer hover:text-blue-600 px-2 py-1 rounded transition-colors"
          onClick={() => onStatusClick(follower)}
          disabled={isLoading}
        >
          View Details
        </button>
      );
    }

    // Default remove/unfollow action
    const getButtonText = () => {
      switch (itemType) {
        case "follower":
          return "Remove";
        case "following":
          return "Unfollow";
        default:
          return "Remove";
      }
    };

    const getButtonIcon = () => {
      switch (itemType) {
        case "follower":
          return closeOutline;
        case "following":
          return closeOutline;
        default:
          return closeOutline;
      }
    };

    return (
      <button
        className={`text-[#0778CF] focus:outline-none p-2 rounded transition-colors ${isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        onClick={() => onRemove(follower)}
        disabled={isLoading}
        aria-label={`${getButtonText()} ${getDisplayName()}`}
        title={getButtonText()}
      >
        <IonIcon
          icon={getButtonIcon()}
          style={{ width: "1.5em", height: "1.5em" , fontSize: "15px"}}
        />
      </button>
    );
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return "Recently";
    }
  };

  return (
    <div className={`flex items-center justify-between p-4 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${isLoading ? "opacity-75" : ""
      }`}>
      {/* Left side - Avatar and user info */}
      <div className="flex items-center w-[65%] min-w-0">
        {/* Avatar */}
        <div className={`w-10 h-10 rounded-full bg-[#007C9E] text-white flex items-center justify-center font-bold shadow-sm flex-shrink-0`}>
          {follower.profile?.avatar ? (
            <img
              src={follower.profile?.avatar}
              alt={getDisplayName()}
              className="w-full h-full rounded-full object-cover"
              onError={() => {
                // F-636: React state only — the style prop below hides the <img>. Never
                // mutate the DOM React owns from an event handler.
                setShowInitials(true);
              }}
              style={{ display: showInitials ? 'none' : 'block' }}
            />
          ) : null}
          {(!follower.profile?.avatar || showInitials) && getInitials()}
        </div>

        {/* User information */}
        <div className="ml-3 flex-1 min-w-0">
          {/* Name */}
          <div className="flex items-center">
            <span
              className="font-medium truncate block capitalize"
              style={{
                maxWidth: "150px",
              }}
              title={getDisplayName()}
            >
              {getDisplayName()}
            </span>
            {/* Premium/Verified badge could go here */}
          </div>

          {/* Status information */}
          {(showStatus || itemType !== "follower") && (
            <div className="mt-1">
              {getStatusDisplay()}
            </div>
          )}

          {/* Contact information */}
          <div className="mt-1 space-y-1">
            {(showEmail || itemType === "pending_incoming") && follower.profile?.email && (
              <div className="flex items-center text-xs text-gray-500">
                <IonIcon icon={mailOutline} className="mr-1" style={{ fontSize: "12px" }} />
                <span className="truncate" style={{ maxWidth: "120px" }} title={follower.profile?.email}>
                  {follower.profile?.email}
                </span>
              </div>
            )}

            {(showPhone || itemType === "pending_incoming") && follower.profile?.phone && (
              <div className="flex items-center text-xs text-gray-500">
                <IonIcon icon={callOutline} className="mr-1" style={{ fontSize: "12px" }} />
                <span className="truncate" style={{ maxWidth: "120px" }} title={follower.profile?.phone}>
                  {follower.profile?.phone}
                </span>
              </div>
            )}
          </div>

          {/* Date information */}
          {/* {follower.created_at && (
            <div className="flex items-center text-xs text-gray-400 mt-1">
              <span>
                {itemType === "follower" ? "Follower since" :
                  itemType === "following" ? "Following since" :
                    itemType === "pending_outgoing" ? "Requested" :
                      "Requested"} {formatDate(follower.created_at)}
              </span>
            </div>
          )} */}
        </div>
      </div>

      {/* Right side - Action button */}
      <div className="flex justify-end items-center w-[35%] flex-shrink-0">
        {getActionButton()}
      </div>
    </div>
  );
};

export default FollowerListItem;